import $ from 'jquery'
import tippy from 'tippy.js'
import { PluginKey } from '@tiptap/pm/state'
import asyncDebounce from '../../../javascript/common/helpers/async_debounce'
import { getExtensionOption, resolveTippyPortal } from '../utilities'

// Allow up to 3 space-separated tokens (First Middle Last) in a mention query.
// Past that, dismiss the popup so a paragraph of typing doesn't keep firing
// member searches — that was bringing the DB to its knees.
const MAX_MENTION_TOKENS = 3

export default {
  pluginKey: new PluginKey('memberMention'),
  allowSpaces: true,
  allowedPrefixes: [' ', '​'], // The second item is the zero-width space character
  allow: ({ state, range }) => {
    const text = state.doc.textBetween(range.from, range.to, '\n', '\0')
    const query = text.replace(/^@/, '')
    return (
      query.trim().split(/\s+/).filter(Boolean).length <= MAX_MENTION_TOKENS
    )
  },
  items: asyncDebounce(({ query, editor }) => {
    const url = getExtensionOption(editor, 'memberMention', 'url')
    if (!url) throw new Error('Member suggestion url is not set')
    return fetchSuggestions(url, query)
  }),
  render: () => {
    let popup
    let mentionsContainer

    return {
      onBeforeStart(obj) {
        const editorContainer = obj.editor.view.dom.closest(
          '.js-tiptap-editor-container'
        )
        const template = document.querySelector(
          '.js-mentions-container-template'
        )
        mentionsContainer = template.content.firstElementChild.cloneNode(true)
        resolveTippyPortal().append(mentionsContainer)
        const menuHTML = editorContainer.dataset['tiptap-EditorMenuValue']
        popup = tippy('body', {
          getReferenceClientRect: obj.clientRect,
          appendTo: () => mentionsContainer,
          content: createMenuDomElement(menuHTML),
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          offset: [10, 0],
          popperOptions: {
            strategy: 'fixed',
          },
        })
      },
      onStart(obj) {
        setContentOnTippy(obj, popup, mentionsContainer)
      },
      onUpdate(obj) {
        setContentOnTippy(obj, popup, mentionsContainer)
      },
      onKeyDown(props) {
        const key = props.event.key
        const menuTarget = popup[0].popper.querySelector(
          "[data-controller='menu']"
        )
        // Don't intercept ArrowRight: this menu has no cols/sections for it
        // to navigate, so capturing it here only blocked the caret from
        // moving past the "@mention" token, trapping the user until they
        // clicked away.
        if (key === 'Escape') {
          props.event.cancelBubble = true
          popup[0].hide()

          return true
        } else if (key === 'ArrowDown') {
          dispatchEventToMenu(menuTarget, 'menu:arrowDown')

          return true
        } else if (key === 'ArrowUp') {
          dispatchEventToMenu(menuTarget, 'menu:arrowUp')

          return true
        } else if (key === 'Enter' || key === 'Tab') {
          props.event.preventDefault()
          dispatchEventToMenu(menuTarget, 'menu:enterKey')

          return true
        }

        return false
      },
      onExit() {
        popup[0].destroy()
        mentionsContainer.remove()
      },
    }
  },
}

const fetchSuggestions = async (url, query) => {
  let returnData
  await $.ajax({
    type: 'POST',
    url,
    data: { prefix: query, path: window.location.pathname },
    success: function (data) {
      returnData = data
    },
    error: function (_xhr, _error, _message) {
      alert("We're sorry, we could not fetch the list of members.")
      returnData = ''
    },
  })
  return returnData
}

// Adding the event listener to anchor tag because MenuComponent selectActiveItem clicks anchor tag to select
// the item. The Enter key shortcut depends on that function.
function removeSelectItemEventListener(container) {
  $(container).off('click', 'li a')
}

function addSelectItemEventListener(container, obj) {
  $(container).on('click', 'li a', function (e) {
    const dataset = e.currentTarget.closest('li').dataset
    obj.command({
      id: dataset.mentionMemberId,
      label: dataset.mentionMemberName,
    })
  })
}

function createMenuDomElement(innerHTML) {
  const menuDomObj = document.createElement('div')
  // bg-bg-alt - class sets the correct background color in dark and light mode
  menuDomObj.classList.add(
    'bg-bg-alt',
    'text-neutral-800',
    'dark:text-neutral-200',
    'rounded-lg',
    'text-sm',
    'shadow-lg',
    'dark:shadow-neutral-850/15',
    'p-2',
    'max-h-132',
    'overflow-auto'
  )
  menuDomObj.innerHTML = innerHTML
  return menuDomObj
}

function dispatchEventToMenu(menuTarget, event) {
  menuTarget.dispatchEvent(new CustomEvent(event))
}

function setContentOnTippy(obj, popup, mentionsContainer) {
  removeSelectItemEventListener(mentionsContainer)
  const menuHTML = obj.items?.content
  if (menuHTML) {
    popup[0].setContent(createMenuDomElement(menuHTML))
  }
  addSelectItemEventListener(mentionsContainer, obj)
}
